package com.steamstreet.vegasful.web.analytics

import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window

/**
 * The single entry point for analytics on the web. Nothing calls `gtag` directly.
 *
 * Every call is fire-and-forget and swallows anything thrown, because a measurement failure must
 * never break the page being measured. gtag is loaded `async` from a third-party host, so on a
 * slow connection — or behind an ad-blocker, which is a good fraction of this audience — it is
 * simply not there when the first call happens. That is an expected state, not an error: the call
 * is dropped and the page carries on.
 *
 * The opt-out check lives here rather than at the call sites, so that no future instrumentation
 * can bypass it. It is enforced a second time in the page head, where the same stored choice sets
 * gtag's own `ga-disable-*` flag before the tag loads — otherwise opting out would still leave
 * GA4's automatic collection running, which is not what somebody clicking the link is asking for.
 */
object Analytics {
    /**
     * Whether the visitor has opted out of measurement.
     *
     * Reading `localStorage` can throw outright — Safari in private browsing, and any browser
     * with site data blocked — so a failure to read is treated as opted out. Choosing the
     * quieter side of an unreadable preference costs a little coverage and cannot misrepresent
     * anyone's choice.
     */
    var isOptedOut: Boolean
        get() = try {
            localStorage.getItem(ANALYTICS_OPT_OUT) == "1"
        } catch (t: Throwable) {
            true
        }
        set(value) {
            try {
                if (value) {
                    localStorage.setItem(ANALYTICS_OPT_OUT, "1")
                } else {
                    localStorage.removeItem(ANALYTICS_OPT_OUT)
                }
                applyCollectionState()
            } catch (t: Throwable) {
                // Nothing useful to do: the preference could not be stored, and saying so on the
                // console is the most a page can honestly offer.
                console.warn("Analytics opt-out could not be stored")
            }
        }

    /** The measurement id the page was configured with, or null when analytics is not configured. */
    val measurementId: String?
        get() = try {
            window.asDynamic().vegasful?.analyticsMeasurementId as? String
        } catch (t: Throwable) {
            null
        }

    /**
     * Record an event. Parameters with a null value are dropped, so an optional property can be
     * passed unconditionally.
     */
    fun track(event: AnalyticsEvent, vararg params: Pair<String, Any?>) {
        track(event, params.toMap())
    }

    fun track(event: AnalyticsEvent, params: Map<String, Any?>) {
        if (isOptedOut) return
        try {
            val gtag = gtag() ?: return
            gtag("event", event.eventName, parameterObject(params))
        } catch (t: Throwable) {
            console.warn("Analytics event ${event.eventName} was not recorded")
        }
    }

    /**
     * Record a screen view.
     *
     * Present for parity with the app's facade, which needs it because single-Activity Compose
     * defeats GA4's automatic screen tracking. The web has real documents and gets automatic page
     * collection, so nothing here calls it — [page] is the web's equivalent. Kept so that code
     * moved between the two surfaces compiles unchanged.
     */
    fun screen(name: String, screenClass: String? = null) {
        track(
            AnalyticsEvent.SCREEN_VIEW,
            AnalyticsParam.SCREEN_NAME to name,
            AnalyticsParam.SCREEN_CLASS to screenClass
        )
    }

    /**
     * Record a view of a page of the site.
     *
     * This is in addition to GA4's automatic `page_view`, not instead of it: the automatic event
     * carries the URL and nothing else, and the two questions that matter for the funnel — what
     * kind of page this is, and whether the visitor is signed in — cannot be asked of it. The two
     * events count the same thing and must never be summed.
     */
    fun page(path: String, signedIn: Boolean) {
        track(
            AnalyticsEvent.PAGE_VIEWED,
            AnalyticsParam.PATH to path,
            AnalyticsParam.PAGE_TYPE to pageTypeOf(path),
            AnalyticsParam.SIGNED_IN to signedIn
        )
    }

    /**
     * Set a user property, skipping the call if the value has not moved since it was last set.
     * A null value clears the local record without reporting, which is what signing out wants.
     */
    fun property(property: UserProperty, value: String?) {
        if (isOptedOut) return
        if (value == null) {
            reportedProperties.remove(property)
            return
        }
        if (reportedProperties[property] == value) return
        reportedProperties[property] = value
        try {
            val gtag = gtag() ?: return
            val properties = js("({})")
            properties[property.propertyName] = value
            gtag("set", "user_properties", properties)
        } catch (t: Throwable) {
            console.warn("User property ${property.propertyName} was not set")
        }
    }

    /**
     * Identify the visitor to analytics. The id is the `sub` claim from the token — never an email
     * address, which Google's terms would not allow anyway.
     *
     * Goes through the facade rather than straight to gtag so that the opt-out covers identity as
     * well as events.
     */
    fun userId(id: String?) {
        if (isOptedOut && id != null) return
        try {
            val gtag = gtag() ?: return
            val config = js("({})")
            config["user_id"] = id
            gtag("set", config)
        } catch (t: Throwable) {
            console.warn("Analytics user id was not set")
        }
    }

    /**
     * Record a save of an event. Fired for both gestures, tagged with which — see [SaveGesture]
     * for why the choice between them is a dashboard filter rather than a code change.
     */
    fun eventSaved(gesture: SaveGesture, eventId: String, source: String?) {
        track(
            AnalyticsEvent.EVENT_SAVED,
            AnalyticsParam.EVENT_ID to eventId,
            AnalyticsParam.GESTURE to gesture.analyticsValue,
            AnalyticsParam.SOURCE to source
        )
    }

    /** The inverse of [eventSaved], tagged the same way. */
    fun eventUnsaved(gesture: SaveGesture, eventId: String) {
        track(
            AnalyticsEvent.EVENT_UNSAVED,
            AnalyticsParam.EVENT_ID to eventId,
            AnalyticsParam.GESTURE to gesture.analyticsValue
        )
    }

    /**
     * Report a failed call to our own API, so that a drop in a funnel can be told from a bug.
     *
     * [message] is truncated hard and is expected to be one of our own descriptions. Nothing that
     * came from the visitor — a search box, an Intelligence question — may be passed here.
     */
    fun apiError(context: String, message: String?, status: Int? = null) {
        track(
            AnalyticsEvent.API_ERROR,
            AnalyticsParam.CONTEXT to context,
            AnalyticsParam.STATUS to status,
            AnalyticsParam.MESSAGE to message?.take(MAX_MESSAGE_LENGTH)
        )
    }

    /**
     * Watch for uncaught errors and rejected promises, and report them as `page_error`.
     *
     * Registered once from the client entry point. The handlers are passive: nothing is prevented
     * and nothing is swallowed, so whatever the browser or another listener would have done still
     * happens.
     */
    fun installErrorReporting() {
        window.addEventListener("error", { event ->
            val message = event.asDynamic().message as? String
            reportPageError(message)
        })
        window.addEventListener("unhandledrejection", { event ->
            val reason = event.asDynamic().reason
            reportPageError((reason?.message as? String) ?: (reason as? String))
        })
    }

    private fun reportPageError(message: String?) {
        track(
            AnalyticsEvent.PAGE_ERROR,
            AnalyticsParam.MESSAGE to (message?.take(MAX_MESSAGE_LENGTH) ?: "unknown"),
            AnalyticsParam.PAGE_TYPE to pageTypeOf(window.location.pathname)
        )
    }

    /**
     * Where the visitor came from, as one of [AnalyticsSource]'s values.
     *
     * A web page knows only its referrer, so this is a weaker signal than the app's navigation
     * stack: it cannot see a back-navigation, and it reports `direct` for anything off-site
     * because that is all the browser is willing to say about most external referrers. It is
     * still the difference between knowing that search leads to ticket clicks and not knowing.
     */
    fun arrivalSource(): String {
        val referrer = try {
            document.referrer
        } catch (t: Throwable) {
            ""
        }
        if (referrer.isEmpty()) return AnalyticsSource.DIRECT
        val origin = window.location.origin
        if (!referrer.startsWith(origin)) return AnalyticsSource.DIRECT
        return when (val type = pageTypeOf(referrer.removePrefix(origin))) {
            "home" -> AnalyticsSource.HOME
            "search" -> AnalyticsSource.SEARCH
            else -> type
        }
    }

    /** Properties already reported, so repeat sets of an unchanged value are dropped. */
    private val reportedProperties = mutableMapOf<UserProperty, String>()

    /**
     * The global `gtag` the head snippet defines, or null when it is not there.
     *
     * It is absent more often than it looks: the snippet defines it synchronously, but only on
     * stages that have a measurement id configured, and an ad-blocker that removes the whole
     * inline block takes it with it. Every caller treats null as "nothing to do".
     */
    private fun gtag(): dynamic {
        val fn = window.asDynamic().gtag
        return if (jsTypeOf(fn) == "function") fn else null
    }

    /**
     * Bring gtag in line with a change to the stored opt-out, for the rest of this page's life.
     * The head snippet applies it on every subsequent load.
     */
    private fun applyCollectionState() {
        val id = measurementId ?: return
        try {
            window.asDynamic()["ga-disable-$id"] = isOptedOut
        } catch (t: Throwable) {
            console.warn("Analytics collection state was not applied")
        }
    }

    /**
     * Build the plain object gtag wants, dropping nulls and anything that is not a type GA4
     * records. An unsupported type is dropped rather than coerced: a parameter that silently
     * becomes `[object Object]` in a report is worse than one that is absent.
     */
    private fun parameterObject(params: Map<String, Any?>): dynamic {
        val target = js("({})")
        params.forEach { (key, value) ->
            when (value) {
                null -> Unit
                is String, is Int, is Double, is Float, is Boolean -> target[key] = value
                // Kotlin/JS models Long as a pair of Ints rather than as a number, so it has to
                // be narrowed here or it would arrive as an object and be dropped by GA4.
                is Long -> target[key] = value.toDouble()
                else -> Unit
            }
        }
        return target
    }

    /** Long enough to identify a failure, short enough that no message becomes a payload. */
    private const val MAX_MESSAGE_LENGTH = 120
}
